import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import {
  AuthManagerService,
  AuthParams,
  UserAuthenticationType,
} from 'src/app/services/auth-manager/auth-manager.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ErrorStrings } from 'src/app/constants/form.constants';
import { Alert } from 'src/app/interfaces/alert';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import {
  RedirectionParams,
  RedirectionService,
} from 'src/app/services/redirection/redirection.service';
import { PetClaimingInfo, PetService } from 'src/app/services/pet/pet.service';
import { ACCOUNT_TYPE_PREVIEW_ROUTE } from 'src/app/constants/route.constants';
import { UserService } from 'src/app/services/user/user.service';
import { ChipService } from 'src/app/services/chip/chip.service';
import { ErrorCode, User } from 'src/app/interfaces/user';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from 'src/app/services/config/config.service';
import { LoginNavigationService } from 'src/app/services/login-navigation/login-navigation.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { errorMessagesMap } from 'src/app/constants/error-messages';

export enum EmailOrPhoneNumberStatus {
  Default,
  HasEmailOrPhoneNumber,
  DontHaveEmailOrPhoneNumber
}

export enum ChipNumberStatus {
  Default,
  HasChipNumber,
  DontHaveChipNumber
}

export enum UserIdentifierType {
  Id,
  EmailAddress,
  PhoneNumber,
  ChipNumber
}

@Component({
  selector: 'app-login-register',
  templateUrl: './login-register.component.html',
  styleUrls: ['./login-register.component.scss'],
})
export class LoginRegisterComponent implements OnInit {
  loading = false;
  alert: Alert = null;
  errorStrings = ErrorStrings;
  adminLoginUrl = environment.adminLoginUrl;
  redirectTimer = 0;
  redirectParams?: RedirectionParams;
  hidePassword: boolean = true;
  user: User;

  authWithChipNumber: boolean = true;
  hasChipNumberStatus: ChipNumberStatus = ChipNumberStatus.Default;
  partialEmail: string = '';
  notValidChipNumber: boolean = false;
  chipNumberInputData: string = '';
  chipNotImplantedError: string = 'chip_not_implanted';
  chipNotFoundError: string = 'chip_not_found';

  hasEmailOrPhoneNumberStatus: EmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.Default;
  userIdentifier: UserIdentifierType = UserIdentifierType.EmailAddress;
  emailAndPhoneNumberInputData: string = '';
  validEmail: boolean = false;
  
  companyPhoneNumber: string;
  companyContactEmailAddress: string;

  isHovered: boolean = false;
  isBrowser: boolean;
  useRebrandedLayout: boolean;

  model: AuthParams = {
    identifier: '',
    password: '',
    authenticationType: UserAuthenticationType.Email,
  }
  petClaimModel: PetClaimingInfo = {
    chipNumber: '',
    vetCode: '',
    vetCodeSkipped: true,
  };
 
  constructor (
    public authManager: AuthManagerService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: RedirectionService,
    private userService: UserService,
    private chipService: ChipService,
    private petService: PetService,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    private loginNavigationService: LoginNavigationService,
    private registrationFlowService: RegistrationFlowService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.companyContactEmailAddress = this.configService.getTextFromKey('contact-email-address');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
  }

  private inputSubject = new Subject<string>();

  public onAuthWithChipNumberChanged(data: number) {
    if (data === 2) {
      this.authWithChipNumber = false;
      return;
    }
    this.authWithChipNumber = true;
    return;
  }

  public redirectToTransferOfKeepership() {
    const base64Encoded = btoa(this.chipNumberInputData);
    if (this.isBrowser) {
      // store chip number in localStorage on ToK button click
      localStorage.setItem('enteredChipNumberToK', base64Encoded);
    }
    this.router.navigate(['/claim-pet'], { queryParams: { chipNumber: base64Encoded } });
  }

  public redirectToRecoverAccount() {
    this.router.navigate(['/forgot-password'], { queryParams: { email: this.emailAndPhoneNumberInputData } });
  }

  public redirectToLogIn() {
    const redirectUrl = this.route.snapshot.queryParams.r;
    if (redirectUrl && redirectUrl !== '/login-register') {
      this.router.navigate(['/login'], { queryParams: { r: redirectUrl }});
      return;
    }
    this.router.navigate(['/login']);
    return;
  }

  public redirectToRegister() {
    if (this.validEmail) {
      const base64Encoded = btoa(this.emailAndPhoneNumberInputData);
      this.router.navigate(['/register'], { queryParams: { email: base64Encoded } });
      return;
    }
    this.router.navigate(['/register']);
    return;
  }

  public onInputChange(value: string): void {

    if (value.length < 15) {
      this.hasChipNumberStatus = ChipNumberStatus.Default;
      this.chipNumberInputData = '';
    }
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue) || (value.length !== 15)) {
      this.notValidChipNumber = true;
      return;
    }
    this.chipNumberInputData = value;
    this.notValidChipNumber = false;
    this.inputSubject.next(value);
    return
  }

  public onInputChangeForEmailOrPhone(value: string): void {
    const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
    const isPhoneNumber = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,11}$/.test(value);

    if (value.length === 0) {
      this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.Default
    }

    if (isEmail) {
      this.validEmail = true;
      this.userIdentifier = UserIdentifierType.EmailAddress;
      this.model.identifier = value
    } else if (isPhoneNumber) {
      this.validEmail = true;
      this.userIdentifier = UserIdentifierType.PhoneNumber;
      this.model.identifier = value
    }

    if (!isEmail && !isPhoneNumber) {
      this.validEmail = false;
      return;
    }
  
    this.notValidChipNumber = false;
    this.inputSubject.next(value);
  }

  onIconHover(isHovered: boolean): void {
    this.isHovered = isHovered;
  }

  checkEmailDbClick() {
    this.closeAlert();
    this.loading = true;
    this.userService.checkEmailOrPhoneNumber(this.emailAndPhoneNumberInputData, this.userIdentifier).subscribe(
      () => {
        this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.HasEmailOrPhoneNumber
      },
      (err) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 404) {
            this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.DontHaveEmailOrPhoneNumber
          }
          if (err.status === 401) {
            this.hasEmailOrPhoneNumberStatus = EmailOrPhoneNumberStatus.DontHaveEmailOrPhoneNumber
          }
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }
      }
    )
    .add(() => {
      this.loading = false;
    });
  }

  checkChipDbClick() {
    this.closeAlert();
    this.loading = true;
    this.chipService.getUserEmailForChipNumber(this.chipNumberInputData).subscribe(
      (response) => {
        this.partialEmail = response.body
        this.hasChipNumberStatus = ChipNumberStatus.HasChipNumber
        this.petClaimModel.chipNumber = this.chipNumberInputData;
      },
      (err) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 404) {
            this.hasChipNumberStatus = ChipNumberStatus.DontHaveChipNumber
            const errorMessage = JSON.parse(err.error)
            if (errorMessage.error_code === this.chipNotImplantedError) {
              this.petClaimModel.chipNumber = this.chipNumberInputData;
              if (this.isBrowser) {
                localStorage.setItem('enteredChipNumber', btoa(this.chipNumberInputData));
              }
            }
            if (errorMessage.error_code === this.chipNotFoundError) {
              this.notValidChipNumber = true;
              this.alert = {
                type: 'danger',
                message: `The chip number could not be found. If the number is correct please give us a call on ${this.companyPhoneNumber} or email ${this.companyContactEmailAddress}`,
              };
            }
          }
          if (err.status === 401) {
            this.hasChipNumberStatus = ChipNumberStatus.DontHaveChipNumber
          }
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }
      }
    )
    .add(() => {
      this.loading = false;
    });
  }
  
  ngOnInit(): void {
    if (this.authManager.isAuthenticated) {
      // The check after login reuses the cached steps, which can be stale this late in the session
      this.registrationFlowService.clear();
      this.loginNavigationService.checkRegistrationProgressOnNextNavigation = true;
      this.router.navigate(['/dashboard']);
    }

    // Prefill email address if "email" is passed as query param
    // (this is set when a redirection from the signup page - 409 status code)
    if (this.route.snapshot.queryParams.email) {
      this.model.identifier = this.route.snapshot.queryParams.email;
      this.alert = {
        type: 'success',
        message: `It looks like you're already registered. Please enter your password to log in.`,
        bold: true,
      }
    }

    if (this.route.snapshot.queryParams.chip) {
      this.model.identifier = this.route.snapshot.queryParams.chip;
      this.authWithChipNumber = true;
      this.alert = {
        type: 'success',
        message: `It looks like chip number ${this.route.snapshot.queryParams.chip} is already registered. Please enter your password to log in.`,
        bold: true,
      }
    }

    // Setting redirection to be used when after login we want the user redirected to a specific page
    this.redirectParams = this.redirectionService.getParams(
      this.route.snapshot.queryParams.r
    );
  }

  login() {
    this.closeAlert();
    this.loading = true;
    this.trackEvent('login::clicked');

    this.model.authenticationType = this.authWithChipNumber
      ? UserAuthenticationType.ChipNumber
      : this.model.identifier.includes('@')
      ? UserAuthenticationType.Email
      : UserAuthenticationType.PhoneNumber;


    this.authManager.login(this.model).subscribe({
      next: (authToken) => {
        if (!authToken) {
          this.redirectToAdmin();
          return;
        }

        if (
        this.redirectParams?.l &&
        !this.redirectParams.l.includes(`/${ACCOUNT_TYPE_PREVIEW_ROUTE}`)
      ) {
        this.router.navigate(this.redirectParams.l, {
          queryParams: this.redirectParams.q,
          fragment: this.redirectParams.f,
        });
      } else {
        // Handle chip number flow before navigating
        if (!this.authWithChipNumber && !this.notValidChipNumber && this.petClaimModel.chipNumber.length !== 0) {
          this.next();
          return;
        }
        this.loginNavigationService.checkRegistrationProgressOnNextNavigation = true;
        this.router.navigate(['/dashboard']);
      }

        return this.userService.getMe();
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.error.status === 400) {
          if (err.error && err.error.errorCode === ErrorCode.MultiplePhoneNumbers) {
            this.alert = {
              type: 'danger',
              message: err.error.message,
            };

          } else if (err.error && err.error.errorCode === ErrorCode.UnknownFailure) {
            this.alert = {
              type: 'danger',
              message: `Please contact identi on ${this.companyPhoneNumber}.`,
            };
          }
          else {
            this.alert = {
              type: 'danger',
              message: `Please contact identi on ${this.companyPhoneNumber}.`,
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 401) {
          if (err.error && err.error.errorCode === ErrorCode.InvalidCredentials) {
            this.alert = {
              type: 'danger',
              message: errorMessagesMap.login_error_message,
            };
          } else if (err.error && err.error.errorCode === ErrorCode.InvalidRole) {
            // Admin users trying to login exception
            this.redirectToAdmin();
          } 
          else {
            this.alert = {
              type: 'danger',
              message: errorMessagesMap.generic_error_message,
            };
          }
        } else if (err instanceof HttpErrorResponse && err.status === 423) {
          // Account temporarily locked after too many failed attempts
          this.alert = {
            type: 'danger',
            message: err.error?.message || errorMessagesMap.generic_error_message,
          };
        } else {
          this.alert = {
            type: 'danger',
            message: 'An error has occurred. Please try again later.',
          };
        }

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  next(): void {
    this.trackEvent('registerchip::chipvetcode::submitted', this.petClaimModel);
    this.closeAlert();
    this.loading = true;
    this.petService
      .claimPet(this.petClaimModel)
      .subscribe(
        (pet) => {
          const queryParam = this.route.snapshot.queryParams;
          if (queryParam.r) {
            this.router.navigate(['/my-pet', pet.id, 'confirm'], {
              queryParams: { r: queryParam.r },
            });
          } else {
            this.router.navigate(['/my-pet', pet.id, 'confirm']);
          }
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            if (err.error && err.error.error_code === 'already_claimed_me') {
              this.alreadyClaimedByMeRedirection();
            } else {
              this.alert = {
                type: 'danger',
                message:
                  "Couldn't complete the request. Please check if the information entered are valid and try again.",
              };
            }
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string, data?: any) {
    if (!this.isBrowser) return;
    window.analytics.track(event, data);
  }

  private redirectToAdmin(): void {
    this.redirectTimer = 3;
    this.showRedirectToAdminMessage();
    if (this.isBrowser) {
      const timer = window.setInterval(() => {
        this.redirectTimer--;
        this.showRedirectToAdminMessage();
        if (!this.redirectTimer) {
          window.clearInterval(timer);
          window.location.href = this.adminLoginUrl;
        }
      }, 1000);
    }
  }

  private alreadyClaimedByMeRedirection(): void {
    const claimedPet = this.user.pets.find(
      (pet) => pet.chipNumber === this.petClaimModel.chipNumber.replace(/\s/g, '')
    );
    if (claimedPet) {
      this.router.navigate(['/my-pet', claimedPet.id]);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private showRedirectToAdminMessage(): void {
    this.alert = {
      type: 'danger',
      message: `It looks like you're not a keeper. You will be redirected automatically in <strong>${this.redirectTimer}s</strong>. If you don't want to wait, please <a href="${this.adminLoginUrl}" rel="nofollow">log in here</a>.`,
    };
  }
};
